import { useMutation, useQuery } from '@apollo/client';
import React, { useState } from 'react';
import * as styles from './UserGroupsSettings.module.scss';
import Button from '../../components/Button';
import Form, { Field, Submit } from '../../components/Form';
import { gql } from '../../graphql/generated/gql';
import SettingCard from '../../settings/SettingCard';

const UPDATE_ORGANIZATION_USER_GROUP_SETTINGS = gql(/** @complexity 4 */ `
  mutation updateOrganizationUserGroupSettings(
    $allowUserGroupCreation: Boolean!
  ) {
    organization: updateOrganization(
      settings: { allowUserGroupCreation: $allowUserGroupCreation }
    ) {
      id
      settings {
        allowUserGroupCreation
      }
    }
  }
`);

const ORGANIZATION_USER_GROUP_SETTINGS_QUERY = gql(/** @complexity 3 */ `
  query OrganizationUserGroupSettings {
    session: currentSession {
      organization {
        id
        settings {
          allowUserGroupCreation
        }
      }
    }
  }
`);

const OPTIONS = {
  enabled: 'Users can directly create new user groups',
  disabled: 'New groups must be requested and approved by an admin',
} as const;

type Values = {
  allowUserGroupCreation: keyof typeof OPTIONS;
};

const UserGroupSettings = () => {
  const [isEditing, setIsEditing] = useState(false);

  const [updateSettings] = useMutation(UPDATE_ORGANIZATION_USER_GROUP_SETTINGS);

  const { data } = useQuery(ORGANIZATION_USER_GROUP_SETTINGS_QUERY);
  if (!data) return null;

  const allowUserGroupCreation = data.session.organization.settings
    ?.allowUserGroupCreation
    ? 'enabled'
    : 'disabled';

  const handleSubmit = async (values: Values) => {
    await updateSettings({
      variables: {
        allowUserGroupCreation: values.allowUserGroupCreation === 'enabled',
      },
    });
    setIsEditing(false);
  };

  return isEditing ? (
    <SettingCard
      title="User groups"
      description={
        <Form<Values>
          initialValues={{ allowUserGroupCreation }}
          onSubmit={handleSubmit}
        >
          <div className={styles.formFields}>
            {(['disabled', 'enabled'] as const).map((value) => (
              <Field
                key={value}
                label={OPTIONS[value]}
                name="allowUserGroupCreation"
                type="radio"
                value={value}
              />
            ))}
          </div>
          <div className={styles.description}>
            Regardless of selection, guests cannot create or request groups.
          </div>
          <div className={styles.formFooter}>
            <Submit variant="primary">Save</Submit>
          </div>
        </Form>
      }
    />
  ) : (
    <SettingCard
      actions={
        <Button onClick={() => setIsEditing(true)} variant="secondary">
          Edit
        </Button>
      }
      title="User groups"
      description={OPTIONS[allowUserGroupCreation]}
    />
  );
};

export default UserGroupSettings;
